/**
 * Question text carries real markup, and rendering it as plain text loses the
 * question.
 *
 * Three kinds appear in the bank, each for a reason:
 *
 *  - **HTML** - `<br>`, `<i>` for scientific names, and `<ins>` for underline.
 *    The underline is not decoration: "Choose the option with the same
 *    consonant as the letter(s) underlined ... re<ins>g</ins>ulate" is
 *    unanswerable without it.
 *  - **LaTeX** in `\(...\)` and `\[...\]` - 251 of 1,000 sampled questions,
 *    using `\frac`, `\sqrt`, `\log`, Greek letters, arrows and matrices. Shown
 *    raw, a maths paper is gibberish.
 *  - **Neither** - `$15,000.00` is a dollar amount and `#3800.00` is Naira, so
 *    `$...$` is *not* treated as a maths delimiter and `#` is not a heading.
 *    Guessing either way corrupts the question.
 *
 * Everything outside the allowlist is escaped. The bank is imported data and a
 * question stem is not a place to trust markup from.
 */

/** Tags that carry meaning in an exam question. Everything else is escaped. */
const ALLOWED_TAGS = ["br", "i", "em", "b", "strong", "ins", "u", "sup", "sub"] as const;

const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

export interface MathSegment {
  latex: string;
  display: boolean;
}

/** Renders one LaTeX expression to HTML. Injected so this file stays pure. */
export type MathRenderer = (segment: MathSegment) => string;

function decodeEntities(text: string): string {
  return text.replace(/&(?:amp|lt|gt|quot|apos|nbsp|#39);/g, (match) => ENTITIES[match] ?? match);
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Put the allowed tags back after escaping.
 *
 * Escape-then-reallow rather than strip-the-bad-ones: a blocklist has to
 * anticipate every way a tag can be written, and this only has to recognise the
 * handful we want. Attributes are dropped with the rest, so there is nowhere to
 * hang an event handler or a `javascript:` url.
 */
function restoreAllowedTags(escaped: string): string {
  const names = ALLOWED_TAGS.join("|");

  // Attributes are matched so they can be thrown away. Keeping the tag and
  // discarding what was written on it beats printing the whole thing as text:
  // the markup a row carries is still meaning, and an `onmouseover` is not.
  return escaped.replace(
    new RegExp(`&lt;(/?)(${names})(?:\\s[^&]*?)?\\s*/?&gt;`, "gi"),
    (_match, closing: string, tag: string) =>
      tag.toLowerCase() === "br" ? "<br />" : `<${closing}${tag.toLowerCase()}>`,
  );
}

/**
 * Some rows carry the same expression twice, once delimited and once bare.
 *
 * A stem like `... \(\frac{x-5}{x(x-1)}\) \frac{x - 5}{x(x - 1)} is undefined`
 * appeared in roughly one question in ten. Rendered as-is the student sees the
 * fraction drawn properly and then the same fraction again as raw source. Only
 * an exact repeat is dropped, ignoring whitespace, so nothing that merely looks
 * similar is touched.
 */
function withoutTrailingDuplicate(latex: string, following: string): string {
  const normalise = (value: string) => value.replace(/\s+/g, "");
  const target = normalise(latex);
  if (target.length === 0) return following;

  const leading = following.match(/^\s*/)?.[0] ?? "";
  const rest = following.slice(leading.length);

  let taken = "";
  for (let index = 0; index < rest.length; index += 1) {
    taken += rest[index];
    const normalised = normalise(taken);
    if (normalised === target) return following.slice(leading.length + index + 1);
    if (normalised.length > target.length) break;
  }

  return following;
}

/**
 * Question text as HTML safe to inject.
 *
 * The order matters: maths comes out first, because LaTeX is full of
 * characters that HTML escaping would mangle, and goes back in last as
 * already-rendered markup.
 */
export function toSafeHtml(raw: string | null | undefined, renderMath: MathRenderer): string {
  if (!raw) return "";

  let remaining = undoubleBackslashes(raw).replace(
    DOLLAR_MATH,
    (_match, body: string) => `\\(${body}\\)`,
  );
  const segments: MathSegment[] = [];
  let withPlaceholders = "";
  let cursor = 0;

  const pattern = /\\\((.+?)\\\)|\\\[(.+?)\\\]/gs;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(remaining)) !== null) {
    const display = match[2] !== undefined;
    const latex = (display ? match[2] : match[1]) ?? "";

    withPlaceholders += remaining.slice(cursor, match.index);
    withPlaceholders += ` MATH${segments.length} `;
    segments.push({ latex, display });

    cursor = match.index + match[0].length;

    const after = remaining.slice(cursor);
    const trimmed = withoutTrailingDuplicate(latex, after);
    if (trimmed.length !== after.length) {
      remaining = remaining.slice(0, cursor) + trimmed;
      pattern.lastIndex = cursor;
    }
  }

  withPlaceholders += remaining.slice(cursor);

  const escaped = restoreAllowedTags(escapeHtml(decodeEntities(withPlaceholders)));

  return withLineBreaks(escaped).replace(/ MATH(\d+) /g, (_match, index: string) => {
    const segment = segments[Number(index)];
    return segment ? renderMath(segment) : "";
  });
}

/**
 * A line break in the text is a line break on screen.
 *
 * HTML collapses `\n` into a space, so a poem excerpt ("His bark is stoutly
 * timbered, / and his pilot") read as one run-on line, and a bookkeeping
 * question's "Cash Sales: ₦30,000 / Credit Sales: ₦50,000" ran together. The
 * maths is already out as placeholders here, so a line break inside `\[…\]`
 * stays LaTeX's whitespace. `\r\n` counts once.
 */
function withLineBreaks(html: string): string {
  return html.replace(/\r\n?|\n/g, "<br />");
}

/**
 * Undo the double-escaping a chunk of the bank was imported with.
 *
 * 113 of 4,997 sampled strings carry `\\log`, `\\sqrt`, `\\(` and so on.
 * The doubled form is not LaTeX: `\\(` leaves a stray backslash inside the
 * expression and KaTeX rejects the whole thing, which is most of the parse
 * failures on the maths papers.
 *
 * Only `\\` immediately followed by a letter or a delimiter is collapsed. A
 * genuine `\\` is LaTeX's line break inside a matrix, where it is followed by
 * a space, and collapsing that would flatten every `pmatrix` in the bank.
 */
function undoubleBackslashes(text: string): string {
  return text.replace(/\\\\(?=[a-zA-Z()[\]])/g, "\\");
}

/**
 * `$...$` is a maths delimiter in some rows and a dollar sign in others.
 *
 * "A trader bought an engine for $15,000.00 ... the exchange rate is $0.075 to
 * N1.00" would otherwise render the sentence between the two amounts as an
 * expression. So a `$...$` pair counts as maths only when what it wraps
 * actually looks like maths — a backslash command or a brace. Money has
 * neither.
 */
const DOLLAR_MATH = /\$([^$\n]*(?:\\[a-zA-Z]+|[{}])[^$\n]*)\$/g;

/** True when the text needs the maths renderer at all. */
export function hasMath(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const normalised = undoubleBackslashes(raw);
  DOLLAR_MATH.lastIndex = 0;
  return /\\\(.+?\\\)|\\\[.+?\\\]/s.test(normalised) || DOLLAR_MATH.test(normalised);
}

/**
 * What to show before the maths renderer has loaded, and if it fails.
 *
 * The delimiters and the most common commands are stripped so the fallback is
 * readable rather than raw source: `\(x^{2}\)` reads as `x^2`, which a student
 * can still work with.
 */
export function plainMath(latex: string): string {
  return latex
    .replace(/\\(?:d|t)?frac\s*\{(.+?)\}\s*\{(.+?)\}/g, "($1)/($2)")
    .replace(/\\sqrt\s*\{(.+?)\}/g, "√($1)")
    .replace(/\\(?:text|mathrm)\s*\{(.+?)\}/g, "$1")
    .replace(/\\times/g, "×")
    .replace(/\\div/g, "÷")
    .replace(/\\pi/g, "π")
    .replace(/\\circ/g, "°")
    .replace(/\\rightarrow|\\to(?![a-zA-Z])/g, "→")
    .replace(/\\le(?![a-zA-Z])/g, "≤")
    .replace(/\\ge(?![a-zA-Z])/g, "≥")
    .replace(/\\neq/g, "≠")
    .replace(/\\pm/g, "±")
    .replace(/\\left|\\right/g, "")
    .replace(/[{}]/g, "")
    .replace(/\\,|\\;|\\!/g, " ")
    .replace(/\\\\/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
